import { BookQualifiedPropertyValuationResponse } from '@/models/mutations/bookQualifiedPropertyValuation';
import { PropertyDetails } from '@/models/queries/getPropertyDetails';
import { OfferType, PropertyTypeNumber } from '@/models/valuationFunnel';
import { ValuationFlow } from '@/types';
import type { HgSupportedLanguage } from '@smg-real-estate/domus';

// following values are same as variable names used in propertyValuation store formData
export const RADIO_FIELDS: Partial<Record<ValuationFlow, string>> = {
  [ValuationFlow.PropertyType]: 'propertyType',
  [ValuationFlow.OfferType]: 'offerType',
  [ValuationFlow.Renovation]: 'isRenovated',
  [ValuationFlow.PropertyOwnership]: 'isPropertyOwner',
  [ValuationFlow.WhenToSell]: 'whenToSell',
};

export enum AddPropertyFormAutoFillInfo {
  SUCCESS = 'success',
  FAILED = 'failed',
  NONE = '',
}

export interface Location {
  lat?: number;
  lon?: number;
  countryCode?: string;
  postalCode?: string;
  city?: string;
  street?: string;
  formattedAddress?: string;
  streetName?: string;
  streetNumber?: string;
}

export interface AgentRecommendation {
  agentId: string;
  firstName: string;
  lastName: string;
  agencyName: string;
  // Not all locales are guaranteed to be provided by the PM data
  agentDescription: Partial<Record<HgSupportedLanguage, string>>;
  location: string;
  yearsOfExp: string;
  agentImageUrl: string;
  agentEmail: string;
  agencyId: string;
}

export interface PropertyValuationData {
  propertyType?: PropertyTypeNumber;
  offerType?: OfferType;
  address: Location;
  livingSpace?: number;
  lotSize?: number | null;
  rooms?: number;
  buildYear?: number;
  isRenovated?: boolean;
  renovationYear?: number | null;
  isPropertyOwner?: boolean;
  email: string;
  name: string;
  phoneNumber: string;
  whenToSell?: number | null;
  culture: string;
  leadId?: string;
  estimationReason?: string | null;
  agentRecommendations?: AgentRecommendation[] | null;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

export interface Lead {
  homegate_lead_id: string;
  email: string;
  confirm_free_consultation: boolean;
}

export interface PropertyValuationState {
  step: number;
  totalSteps: number;
  previousStep: number;
  loading: boolean;
  data: PropertyValuationData;
  lead: Lead;
  propertyDetailsData: Array<PropertyDetails>;
  selectedPropertyDetailIndex: number;
  isValidLocation: boolean | undefined;
  locationAlternatives: Array<Location>;
  isIframeActive: boolean;
}

export type PostQualifiedValuationResult = Promise<{
  isSuccess: boolean;
  result: BookQualifiedPropertyValuationResponse | null;
  statusCode?: number;
}>;

export interface Address {
  lat: number;
  lon: number;
  countryCode: string;
  postalCode: string;
  city: string;
  street: string;
}

export interface RentBreakdown {
  /**
   * Gross rent per month
   */
  gross?: number;
  /**
   * Gross rent per month per square meter
   */
  grossPerSqm?: number;
  /**
   * Net rent per month
   */
  net?: number;
  /**
   * Net rent per month per square meter
   */
  netPerSqm?: number;
  /**
   * Date for the last time the data was updated
   */
  lastUpdatedAt: string;
}

export interface PriceEvolutionDataPoint {
  date: string;
  indexValue: number;
}

export interface PriceEvolution {
  data: Array<PriceEvolutionDataPoint>;
  lastUpdatedAt: string;
}

export interface PropertyCommonDataItemResponse {
  ewid?: string;
  livingSpace?: number;
  numberOfRooms?: number;
  lotSize?: number;
  buildYear?: number;
  floor?: number;
}
export interface PropertyCommonDataResponse {
  items?: Array<PropertyCommonDataItemResponse>;
  propertyType?: PropertyTypeNumber;
}

export interface HomeOwnerWorldPropertyBase {
  createdDate?: string;
  modifiedDate?: string;
  propertyType: PropertyTypeNumber;
  estimationReason?: string;
  address: Address;
  culture: string;
  ortId?: number;
  municipalityId?: number;
  municipalityLabel?: string;
  municipalityPriceDevelopment?: string;
  municipalityPopulationGrowth?: string;
  municipalityResidentialConstruction?: string;
  municipalityVacancy?: string;
  municipalityTax?: string;
  addressValidationStatus?: string;
  offerType: OfferType;
  source?: string;
  category?: string;
  livingSpace: number;
  lotSize?: number;
  room: number;
  yearBuild: number;
  isRenovated: boolean;
  renovationYear?: number;
  whenToSell?: number | null;
  evaluatedPriceFrom?: number;
  evaluatedPriceTo?: number;
  appraisalPrice?: number;
  appraisalDate?: string;
  locationId?: number;
  needBroker?: boolean;
  isPropertyOwner?: boolean;
  estimatedOfferedRent?: RentBreakdown;
  priceEvolution?: PriceEvolution;
  phoneNumber?: string;
  isConsultantRequested?: boolean;
  intent?: string;
}

export interface HomeOwnerWorldPropertyCreateRequest extends HomeOwnerWorldPropertyBase {
  propertyId?: string;
}

export interface HomeOwnerWorldPropertyUpdateRequest {
  isRenovated?: boolean;
  livingSpace?: number;
  offerType?: OfferType;
  propertyType?: PropertyTypeNumber;
  room?: number;
  yearBuild?: number;
  lotSize?: number;
  renovationYear?: number;
  whenToSell?: number | null;
  isConsultantRequested?: boolean | null;
  phoneNumber?: string | null;
  intent?: string;
}

export interface HomeOwnerWorldPropertyResponse extends HomeOwnerWorldPropertyBase {
  propertyId: string;
  revaluationTriggered?: boolean;
  remainingValuations?: number;
}

export interface HomeOwnerWorldPropertiesResponse {
  data: HomeOwnerWorldProperty[];
  meta?: {
    canUserCreate: boolean;
    total: number;
  };
}

export type HomeOwnerWorldProperty = HomeOwnerWorldPropertyResponse & {
  personId: string;
  leadId?: string;
};

export interface HomeOwnerWorldPropertyLinkResponse {
  propertyId: string;
}
export interface HomeOwnerWorldValuationFormData {
  propertyType: number;
  offerType: number;
  address: Location;
  livingSpace: number;
  lotSize?: number;
  rooms: number;
  buildYear: number;
  isRenovated: boolean;
  renovationYear?: number;
  whenToSell?: number;
  culture: string;
  isConsultantRequested?: boolean;
  phoneNumber?: string;
}
